import React, { useEffect, useState } from "react";
import { api, fmtMoney, fmtPct, monthLabel } from "@/lib/api";
import { X } from "lucide-react";
import BulletHole from "./BulletHole";

const pad = (n) => String(n).padStart(2, "0");

export default function CommandDeck({ open, onClose }) {
  const [data, setData] = useState(null);
  const [brief, setBrief] = useState(null);
  const [now, setNow] = useState(new Date());

  useEffect(() => {
    if (!open) return;
    const load = async () => {
      const [d, b] = await Promise.all([
        api.get("/dashboard").then((r) => r.data),
        api.get("/briefing").then((r) => r.data).catch(() => null),
      ]);
      setData(d);
      setBrief(b);
    };
    load();
    const t = setInterval(() => setNow(new Date()), 1000);
    const refresh = setInterval(load, 30000);
    return () => {
      clearInterval(t);
      clearInterval(refresh);
    };
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => {
      if (e.key === "Escape" || e.key.toLowerCase() === "f") {
        e.preventDefault();
        onClose();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  if (!open) return null;

  const timeBig = `${pad(now.getHours())}:${pad(now.getMinutes())}`;
  const seconds = pad(now.getSeconds());
  const dateLong = now.toLocaleDateString("en-US", {
    weekday: "long",
    month: "long",
    day: "numeric",
    year: "numeric",
  });

  return (
    <div
      className="fixed inset-0 z-[60] bg-background animate-fadein overflow-hidden"
      data-testid="command-deck"
    >
      {/* tread-plate corners */}
      <div className="tread-plate absolute top-0 left-0 right-0 h-2 opacity-60" />
      <div className="tread-plate absolute bottom-0 left-0 right-0 h-2 opacity-60" />

      {/* bullet holes */}
      <BulletHole
        variant={2}
        size={140}
        className="absolute top-8 right-12 opacity-70"
      />
      <BulletHole
        variant={3}
        size={100}
        className="absolute bottom-16 left-10 opacity-50"
      />

      {/* close */}
      <button
        onClick={onClose}
        className="absolute top-6 left-6 hud-btn flex items-center gap-2 z-10"
        data-testid="deck-close"
      >
        <X size={11} /> EXIT [F]
      </button>

      {/* tag */}
      <div className="absolute top-6 right-1/2 translate-x-1/2 readout">
        <span className="status-dot" /> <strong>LIVE</strong> · COMMAND DECK
      </div>

      <div className="h-full flex flex-col justify-center px-6 lg:px-16 max-w-[1800px] mx-auto relative z-[2]">
        {/* Top — clock + date */}
        <div className="flex items-end justify-between flex-wrap gap-6 mb-12">
          <div>
            <div className="kpi-label mb-2">SYSTEM TIME</div>
            <div className="display font-bold leading-none flex items-baseline">
              <span style={{ fontSize: "clamp(5rem, 16vw, 14rem)" }}>{timeBig}</span>
              <span className="text-hud mono ml-3 sm:ml-6 font-medium" style={{ fontSize: "clamp(2rem, 5vw, 4rem)" }}>
                :{seconds}
              </span>
            </div>
            <div className="display text-xl sm:text-2xl mt-2 uppercase tracking-wider text-muted-foreground">
              {dateLong}
            </div>
          </div>
          <div className="text-right">
            <div className="kpi-label">CYCLE</div>
            <div className="display text-3xl sm:text-4xl mt-1 font-semibold uppercase">
              {data ? monthLabel(data.month) : "—"}
            </div>
          </div>
        </div>

        {data && (
          <>
            {/* 4 mega KPIs */}
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-10">
              {[
                { label: "Monthly Habits", value: fmtPct(data.habit_pct) },
                { label: "Weekly Output", value: fmtPct(data.week_pct) },
                { label: "Pipeline Open", value: fmtMoney(data.projected_commission) },
                { label: "Media Revenue", value: fmtMoney(data.media_revenue) },
              ].map((k) => (
                <div key={k.label} className="hud-frame p-6">
                  <div className="kpi-label">{k.label}</div>
                  <div className="display font-bold mt-3 leading-none tabular-nums"
                       style={{ fontSize: "clamp(2.5rem, 6vw, 5rem)" }}>
                    {k.value}
                  </div>
                </div>
              ))}
            </div>

            {/* Verdict */}
            {brief && (
              <div className="hud-frame p-6 lg:p-8 relative">
                <div className="kpi-label">FIELD REPORT</div>
                <p className="display text-2xl sm:text-3xl lg:text-4xl mt-3 leading-snug font-medium">
                  {brief.verdict}
                </p>
              </div>
            )}

            {/* Bottom ticker */}
            <div className="mt-10 flex flex-wrap items-center gap-x-8 gap-y-2 mono text-[11px] text-muted-foreground uppercase tracking-[0.24em]">
              <span><strong className="text-foreground">{data.closed_deals}</strong> closed</span>
              <span><strong className="text-foreground">{data.active_deals}</strong> active</span>
              <span><strong className="text-foreground">{data.media_orders}</strong> orders</span>
              <span><strong className="text-foreground">{data.media_paid}</strong> paid</span>
              <span><strong className="text-foreground">{data.media_completed}</strong> delivered</span>
              <span className="flex-1" />
              <span>press <strong className="text-foreground">F</strong> or <strong className="text-foreground">ESC</strong> to return</span>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
